import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, Check, X, Copy, ExternalLink, Settings, ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WhatsAppSetup = ({ onBack }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [configuring, setConfiguring] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const [credentials, setCredentials] = useState({
    account_sid: '',
    auth_token: '',
    whatsapp_number: 'whatsapp:+14155238886'
  });

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API}/whatsapp/status`);
      setStatus(response.data);
    } catch (err) {
      console.error('Failed to fetch status:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleConfigure = async (e) => {
    e.preventDefault();
    setConfiguring(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await axios.post(`${API}/whatsapp/configure`, credentials);
      setSuccess(response.data.message);
      fetchStatus();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to configure Twilio');
    } finally {
      setConfiguring(false);
    }
  };

  const copyWebhookUrl = () => {
    if (status?.webhook_url) {
      navigator.clipboard.writeText(status.webhook_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen min-h-[100dvh] bg-[#FFF9E6] flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#E6A817]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen min-h-[100dvh] bg-[#FFF9E6]">
      {/* Mobile-optimized Header */}
      <div className="sticky top-0 z-10 bg-gradient-to-r from-[#E6A817] to-[#F4C430] px-4 py-3 safe-area-top">
        <div className="flex items-center gap-3 max-w-2xl mx-auto">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={onBack}
            className="rounded-full bg-white/30 hover:bg-white/50 text-gray-900 h-10 w-10"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-gray-900 font-['Outfit'] truncate">WhatsApp Integration</h1>
            <p className="text-gray-700 text-xs truncate">Connect to your WhatsApp</p>
          </div>
        </div>
      </div>

      {/* Content with proper padding */}
      <div className="px-4 py-4 pb-8 max-w-2xl mx-auto space-y-4">
        {/* Status Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="border-0 shadow-md overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-[#E6A817] to-[#F4C430] text-gray-900 py-4 px-4">
              <CardTitle className="flex items-center gap-2 text-base">
                <MessageCircle className="w-5 h-5" />
                Connection Status
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 px-4 pb-4">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full flex-shrink-0 ${status?.configured ? 'bg-green-500' : 'bg-amber-500'}`}></div>
                <span className="font-medium text-sm">
                  {status?.configured ? 'Connected to Twilio' : 'Not Connected'}
                </span>
              </div>
              
              {status?.configured && (
                <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-600">WhatsApp Number:</p>
                  <p className="font-mono font-medium text-sm break-all">{status.whatsapp_number}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Webhook URL Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="border-0 shadow-md">
            <CardHeader className="py-4 px-4">
              <CardTitle className="text-base">Webhook URL</CardTitle>
              <CardDescription className="text-xs">
                Copy and paste in Twilio Console
              </CardDescription>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <div className="flex gap-2">
                <Input 
                  value={status?.webhook_url || ''} 
                  readOnly 
                  className="font-mono text-xs bg-gray-50 h-11"
                  data-testid="webhook-url"
                />
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={copyWebhookUrl}
                  className="h-11 w-11 flex-shrink-0"
                  data-testid="copy-webhook-btn"
                >
                  {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Set as "When a message comes in" webhook
              </p>
            </CardContent>
          </Card>
        </motion.div>

        {/* Configuration Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="border-0 shadow-md">
            <CardHeader className="py-4 px-4">
              <CardTitle className="flex items-center gap-2 text-base">
                <Settings className="w-4 h-4" />
                Twilio Credentials
              </CardTitle>
              <CardDescription className="text-xs">
                Enter your Twilio credentials
              </CardDescription>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              {error && (
                <Alert variant="destructive" className="mb-4 py-2">
                  <X className="w-4 h-4" />
                  <AlertDescription className="text-sm">{error}</AlertDescription>
                </Alert>
              )}
              
              {success && (
                <Alert className="mb-4 py-2 bg-green-50 border-green-200">
                  <Check className="w-4 h-4 text-green-600" />
                  <AlertDescription className="text-sm text-green-800">{success}</AlertDescription>
                </Alert>
              )}

              <form onSubmit={handleConfigure} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1.5">
                    Account SID
                  </label>
                  <Input
                    type="text"
                    placeholder="ACxxxxxxxxxxxxxxxx"
                    value={credentials.account_sid}
                    onChange={(e) => setCredentials(prev => ({ ...prev, account_sid: e.target.value }))}
                    required
                    className="h-12 text-base"
                    data-testid="account-sid-input"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1.5">
                    Auth Token
                  </label>
                  <Input
                    type="password"
                    placeholder="Your Twilio Auth Token"
                    value={credentials.auth_token}
                    onChange={(e) => setCredentials(prev => ({ ...prev, auth_token: e.target.value }))}
                    required
                    className="h-12 text-base"
                    data-testid="auth-token-input"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1.5">
                    WhatsApp Number
                  </label>
                  <Input
                    type="text"
                    placeholder="whatsapp:+14155238886"
                    value={credentials.whatsapp_number}
                    onChange={(e) => setCredentials(prev => ({ ...prev, whatsapp_number: e.target.value }))}
                    required
                    className="h-12 text-base"
                    data-testid="whatsapp-number-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Use Twilio Sandbox number for testing
                  </p>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-[#E6A817] hover:bg-[#C48F0F] h-12 text-base text-gray-900"
                  disabled={configuring}
                  data-testid="configure-btn"
                >
                  {configuring ? 'Configuring...' : 'Configure Twilio'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </motion.div>

        {/* Setup Instructions - Collapsible on Mobile */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card className="border-0 shadow-md">
            <CardHeader className="py-4 px-4">
              <CardTitle className="text-base">Setup Instructions</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <ol className="space-y-3 text-sm">
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-[#E6A817] text-gray-900 rounded-full flex items-center justify-center text-xs font-bold">1</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">Create a Twilio account</p>
                    <a 
                      href="https://www.twilio.com/try-twilio" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-[#C48F0F] hover:underline inline-flex items-center gap-1 text-xs"
                    >
                      Sign up at Twilio <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-[#E6A817] text-gray-900 rounded-full flex items-center justify-center text-xs font-bold">2</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">Enable WhatsApp Sandbox</p>
                    <p className="text-gray-600 text-xs">Messaging → Try it out → WhatsApp</p>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-[#E6A817] text-gray-900 rounded-full flex items-center justify-center text-xs font-bold">3</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">Join the Sandbox</p>
                    <p className="text-gray-600 text-xs">Send join code to Twilio number</p>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-[#E6A817] text-gray-900 rounded-full flex items-center justify-center text-xs font-bold">4</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">Configure Webhook</p>
                    <p className="text-gray-600 text-xs">Paste URL in Twilio settings</p>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-[#E6A817] text-gray-900 rounded-full flex items-center justify-center text-xs font-bold">5</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">Enter Credentials Above</p>
                    <p className="text-gray-600 text-xs">Get from Twilio Console</p>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-[#E6A817] text-gray-900 rounded-full flex items-center justify-center text-xs font-bold">6</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">Test it!</p>
                    <p className="text-gray-600 text-xs">Send "MENU" to start</p>
                  </div>
                </li>
              </ol>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default WhatsAppSetup;
